import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { ShieldCheck, Camera, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUploadPhoto } from "@/hooks/api/usePhotos";
import { useCBSBatchStore } from "@/stores/cbsBatchStore";
import type { Database } from "@/integrations/supabase/types";

type PhotoCategory = Database["public"]["Enums"]["photo_category"];

const PHOTO_SLOTS = [
  { key: "entrance", emoji: "🏠", label: "Shed Entrance", category: "shed" as PhotoCategory },
  { key: "shed", emoji: "🐔", label: "Birds & Interior", category: "shed" as PhotoCategory },
  { key: "surroundings", emoji: "🌍", label: "Farm Surroundings", category: "equipment" as PhotoCategory },
] as const;

export const BiosecurityLogScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isSimpleMode } = useSimpleMode();
  const uploadPhoto = useUploadPhoto();
  const activeBatch = useCBSBatchStore((s) => s.activeBatch);

  const [photos, setPhotos] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [activeSlot, setActiveSlot] = useState<string | null>(null);

  const photosTaken = PHOTO_SLOTS.filter((s) => photos[s.key]).length;
  const allTaken = photosTaken === 3;

  const handleCaptureClick = (key: string) => {
    setActiveSlot(key);
    fileInputRefs.current[key]?.click();
  };

  const handleFileSelected = async (key: string, category: PhotoCategory, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if ("vibrate" in navigator) navigator.vibrate(15);
    setUploading((prev) => ({ ...prev, [key]: true }));

    try {
      await uploadPhoto.mutateAsync({
        file,
        photoCategory: category,
        batchId: activeBatch?.id,
      });
      setPhotos((prev) => ({ ...prev, [key]: true }));
      toast({ title: "📸 Photo captured" });
    } catch {
      // Error handled by useUploadPhoto
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
      e.target.value = "";
    }
  };

  const handleSubmit = () => {
    setIsSaving(true);
    toast({ title: "🛡️ Biosecurity assessment saved" });
    setShowSuccess(true);
    if ("vibrate" in navigator) navigator.vibrate([50, 50, 100]);
    setTimeout(() => navigate("/"), 1500);
  };

  const title = isSimpleMode
    ? "🛡️"
    : t("cbs.biosecurity.title", { defaultValue: "🛡️ Biosecurity Check" });

  return (
    <Screen
      showTopBar
      showBottomNav={false}
      topBarProps={{
        title,
        showBack: true,
        showMapPin: false,
        showSettings: false,
        onBack: () => navigate('/'),
      }}
    >
      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-7 h-7 text-primary" />
          {!isSimpleMode && (
            <p className="text-sm text-muted-foreground">
              Take 3 photos of your farm for the biosecurity check.
            </p>
          )}
        </div>

        {/* Photo cards */}
        {PHOTO_SLOTS.map((slot) => {
          const taken = !!photos[slot.key];
          const isUploading = !!uploading[slot.key];
          return (
            <Card
              key={slot.key}
              className={cn(
                "p-4 transition-all",
                taken
                  ? "border-2 border-success bg-success/10"
                  : "border-2 border-dashed border-muted-foreground/30 bg-muted/50"
              )}
            >
              <input
                ref={(el) => { fileInputRefs.current[slot.key] = el; }}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileSelected(slot.key, slot.category, e)}
              />
              <button
                type="button"
                onClick={() => !taken && !isUploading && handleCaptureClick(slot.key)}
                disabled={taken || isUploading}
                className={cn(
                  "w-full flex items-center gap-4 rounded-xl transition-all active:scale-95",
                  isSimpleMode ? "justify-center py-4" : "justify-start py-2"
                )}
              >
                {isSimpleMode ? (
                  <span className="text-4xl">
                    {isUploading ? "⏳" : taken ? "✅" : slot.emoji}
                  </span>
                ) : (
                  <>
                    {isUploading ? (
                      <Loader2 className="w-6 h-6 text-muted-foreground flex-shrink-0 animate-spin" />
                    ) : taken ? (
                      <Check className="w-6 h-6 text-success flex-shrink-0" />
                    ) : (
                      <Camera className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="text-left">
                      <p className="font-medium text-foreground">
                        {slot.emoji} {slot.label}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isUploading ? "⏳ Uploading..." : taken ? "✅ Captured" : "Tap to take photo"}
                      </p>
                    </div>
                  </>
                )}
              </button>
            </Card>
          );
        })}

        {/* Counter */}
        <p
          className={cn(
            "text-center font-semibold",
            allTaken ? "text-success" : "text-muted-foreground",
            isSimpleMode ? "text-2xl" : "text-base"
          )}
        >
          {isSimpleMode ? `${photosTaken}/3 📸` : `${photosTaken}/3 photos taken`}
        </p>

        {/* Submit */}
        <Button
          className={cn(
            "w-full gap-2",
            isSimpleMode ? "h-20 text-2xl" : "h-16 text-xl"
          )}
          onClick={handleSubmit}
          disabled={isSaving || !allTaken}
        >
          {isSaving ? (
            <>
              <span className="animate-spin">⏳</span>
              {!isSimpleMode && t("common.saving", { defaultValue: "Saving..." })}
            </>
          ) : (
            <>
              {isSimpleMode ? "✅" : <Check className="w-6 h-6" />}
              {!isSimpleMode && t("common.save", { defaultValue: "Save" })}
            </>
          )}
        </Button>
      </div>

      {/* Celebration overlay */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="text-8xl"
            >
              🎉
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 text-xl font-semibold text-foreground"
            >
              Saved!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </Screen>
  );
};

export default BiosecurityLogScreen;
